// Developer: Ankit Gupta
/**
 * What to say when a write fails.
 *
 * A mutation that fails silently is worse than one that fails loudly: the
 * screen still shows the old state, so the user believes the change landed.
 * Every mutation in Insight reports through here, and every message names what
 * did not happen and what to do next.
 */
import { useCallback } from 'react';
import { App } from 'antd';
import i18n from '@/i18n';

interface ServerError {
  response?: { status?: number; data?: unknown };
  message?: string;
}

/** The server's own explanation, when it sent one. */
function serverDetail(error: unknown): string | null {
  const data = (error as ServerError)?.response?.data as { detail?: unknown } | undefined;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item && typeof item === 'object' ? (item as { msg?: unknown }).msg : null))
      .filter((msg): msg is string => typeof msg === 'string' && msg.trim() !== '');
    if (messages.length > 0) return messages.join('; ');
  }
  return null;
}

/**
 * One sentence explaining why, and what the person can do about it.
 *
 * The server's own wording wins when there is one, because it knows the
 * specific rule that refused; the status code only fills the silence.
 */
export function failureReason(error: unknown): string {
  const detail = serverDetail(error);
  if (detail) return detail;

  const response = (error as ServerError)?.response;
  if (!response) {
    return i18n.t('insightPages:errors.unreachable');
  }
  const status = response.status ?? 0;
  if (status === 401) return i18n.t('insightPages:errors.expired');
  if (status === 403) return i18n.t('insightPages:errors.forbidden');
  if (status === 404) return i18n.t('insightPages:errors.notFound');
  if (status === 409) return i18n.t('insightPages:errors.conflict');
  if (status === 422) return i18n.t('insightPages:errors.invalid');
  if (status === 429) return i18n.t('insightPages:errors.tooMany');
  if (status >= 500) return i18n.t('insightPages:errors.server');
  return i18n.t('insightPages:errors.generic');
}

/**
 * Report a failed write.
 *
 * `headline` names what did not happen ("The tile could not be removed."),
 * and the reason is appended, so the message always answers both questions.
 */
export function useFailureMessage(): (headline: string, error: unknown) => void {
  const { message } = App.useApp();
  return useCallback(
    (headline: string, error: unknown) => {
      message.error(`${headline} ${failureReason(error)}`, 6);
    },
    [message],
  );
}
